import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash-es/isEqual';

import type { ArticleSortOption } from 'common/enums/SortOption';
import { trackDrawerFilterChange } from '../tracking/filters';
import type { Filter } from '../types/SortAndFilter';
import { getInitialQueryParams } from '../utils/filters';
import type { QueryParams } from '../utils/url';
import { getCurrentUrl, getUrlWithQueryParams } from '../utils/url';

interface UseFilterQueryParamsProps {
  filters: Filter[];
  order: ArticleSortOption;
}

const getFilterKeys = (filters: Filter[]): Set<string> => {
  const keys = new Set<string>();

  filters.forEach(filter => {
    if ('options' in filter) {
      // Option filters: color, brand, etc.
      keys.add(filter.key);
    } else {
      // Range filters: price.min, price.max, etc.
      keys.add(`${filter.key}Min`);
      keys.add(`${filter.key}Max`);
    }
  });

  return keys;
};

/**
 * Finds filter values that exist in URL but not in drawer options
 * Example: URL has "color=red|teal" but drawer only shows [red, blue, green]
 * Returns: {color: ["teal"]} - values that exist only in URL
 */
const getUrlOnlyValues = (filters: Filter[]): Record<string, string[]> => {
  const url = getCurrentUrl();
  if (!url) return {};

  let urlSearchParams: URLSearchParams;
  try {
    urlSearchParams = new URL(url).searchParams;
  } catch {
    return {};
  }

  const urlOnlyValues: Record<string, string[]> = {};

  filters.forEach(filter => {
    if (!('options' in filter)) return; // Skip range filters

    const urlValue = urlSearchParams.get(filter.key);
    if (!urlValue) return;

    const valuesInUrl = urlValue.split('|');
    const valuesInDrawer = new Set(filter.options.map(opt => opt.key));
    const onlyInUrl = valuesInUrl.filter(val => !valuesInDrawer.has(val));

    if (onlyInUrl.length > 0) {
      urlOnlyValues[filter.key] = onlyInUrl;
    }
  });

  return urlOnlyValues;
};

const normalizeValues = (values: string[]): string => {
  if (values.length === 0) return '';

  // Removes duplicates and sorts values alphabetically for consistent URLs
  return [...new Set(values)].sort().join('|');
};

/**
 * Adds URL-only values to drawer values
 * Example: drawerValue="blue|green" + urlOnlyValues=["teal"] → "blue|green|teal"
 */
const addUrlOnlyValues = (
  drawerValue: string | number | boolean,
  urlOnlyValues: string[]
): string => {
  if (drawerValue === '' || drawerValue === undefined) {
    // No drawer values, return only URL values
    return normalizeValues(urlOnlyValues);
  }

  const drawerValues = drawerValue.toString().split('|');

  return normalizeValues([...drawerValues, ...urlOnlyValues]);
};

const mergeParams = (urlParams: QueryParams, drawerParams: QueryParams): QueryParams => {
  const result: QueryParams = { ...drawerParams };

  // Merges URL params with drawer params
  Object.keys(urlParams).forEach(key => {
    const urlValue = urlParams[key];
    const drawerValue = drawerParams[key];

    if (!urlValue || urlValue === '') return;

    if (drawerValue === undefined || drawerValue === '') {
      result[key] = urlValue;

      return;
    }

    if (typeof urlValue === 'string' && typeof drawerValue === 'string') {
      const urlValues = urlValue.split('|');
      const drawerValues = drawerValue.split('|');
      result[key] = normalizeValues([...drawerValues, ...urlValues]);
    }
  });

  return result;
};

/**
 * Initializes filter state by merging URL with drawer state
 * Preserves URL-only values and non-filter params (campaignKey, utm params, etc.)
 */
const computeFilterQueryParams = (
  filters: Filter[],
  initialQueryParams: QueryParams
): QueryParams => {
  const url = getCurrentUrl();
  if (!url) return initialQueryParams;

  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    return initialQueryParams;
  }

  const filterKeys = getFilterKeys(filters);

  // Separate URL params into filter params vs non-filter params
  const nonFilterParams: QueryParams = {}; // campaignKey, utm_source, etc.
  const filterUrlParams: QueryParams = {}; // color, brand, price, etc.

  urlObj.searchParams.forEach((value, key) => {
    if (key === 'page' || key === 'order') return; // Skip pagination/sort

    if (filterKeys.has(key)) {
      filterUrlParams[key] = value;
    } else {
      nonFilterParams[key] = value;
    }
  });

  const mergedFilterParams = mergeParams(filterUrlParams, initialQueryParams);

  return {
    ...nonFilterParams,
    ...mergedFilterParams,
  };
};

const applyFilterChanges = (
  queryParams: QueryParams,
  filterQueryParams: QueryParams,
  filters: Filter[]
): QueryParams => {
  const urlOnlyValues = getUrlOnlyValues(filters);
  const updatedParams = { ...filterQueryParams };

  Object.keys(queryParams).forEach(key => {
    const newValue = queryParams[key];
    const urlOnly = urlOnlyValues[key] || [];

    updatedParams[key] =
      typeof newValue === 'string' ? addUrlOnlyValues(newValue, urlOnly) : newValue;
  });

  return updatedParams;
};

export const useFilterQueryParams = ({ filters, order }: UseFilterQueryParamsProps) => {
  const initialQueryParams = useMemo(() => getInitialQueryParams(filters, order), [filters, order]);

  const [filterQueryParams, setFilterQueryParams] = useState<QueryParams>(() =>
    computeFilterQueryParams(filters, initialQueryParams)
  );

  useEffect(() => {
    setFilterQueryParams(computeFilterQueryParams(filters, getInitialQueryParams(filters, order)));
  }, [filters, order]);

  const onFilterChange = useCallback(
    (queryParams: QueryParams) => {
      trackDrawerFilterChange(filterQueryParams, queryParams);

      const updatedParams = applyFilterChanges(queryParams, filterQueryParams, filters);

      setFilterQueryParams(updatedParams);
    },
    [filterQueryParams, filters]
  );

  const url = useMemo(() => getUrlWithQueryParams(filterQueryParams), [filterQueryParams]);

  const haveFiltersChanged = useMemo(
    () => isEqual(initialQueryParams, filterQueryParams),
    [initialQueryParams, filterQueryParams]
  );

  return {
    onFilterChange,
    url,
    haveFiltersChanged,
  };
};
